import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw } from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import PlantillaModal from '../util/PlantillaModal';

const LIMITE_PAGINA = 20;

const formatMonto = (monto) => {
  return parseFloat(monto || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatFechaHora = (fecha) => {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleString('es-DO', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
};

export default function ModalResumenRetirosSesion({ mostrar, onClose, idSesionCaja, nombreCaja }) {
  const [retiros, setRetiros] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [cargandoInicial, setCargandoInicial] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);

  const cargarRetiros = async ({ append = false, cursor = null } = {}) => {
    if (!idSesionCaja) return;

    try {
      if (append) {
        setCargandoMas(true);
      } else {
        setCargandoInicial(true);
      }

      const params = { limit: LIMITE_PAGINA };
      if (cursor?.cursor_fecha && cursor?.cursor_id) {
        params.cursor_fecha = cursor.cursor_fecha;
        params.cursor_id = cursor.cursor_id;
      }

      const response = await instanciaAxios.get(`/sesion-caja/${idSesionCaja}/retiros-resumen`, { params });
      const nuevosRetiros = response.data?.retiros || [];

      setRetiros((prev) => append ? [...prev, ...nuevosRetiros] : nuevosRetiros);
      setHasMore(Boolean(response.data?.has_more));
      setNextCursor(response.data?.next_cursor || null);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar resumen de retiros:', error);
      toast.error('Error al cargar resumen de retiros');
    } finally {
      setCargandoInicial(false);
      setCargandoMas(false);
    }
  };

  const recargar = async () => {
    setRetiros([]);
    setHasMore(false);
    setNextCursor(null);
    await cargarRetiros();
  };

  useEffect(() => {
    if (!mostrar || !idSesionCaja) return;
    recargar();
  }, [mostrar, idSesionCaja]);

  return (
    <PlantillaModal
      mostrar={mostrar}
      onClose={onClose}
      titulo={`Resumen de retiros - ${nombreCaja || 'Caja'}`}
      maxWidth="max-w-6xl"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Retiros cargados: <span className="font-semibold">{retiros.length}</span>
          </p>
          <button
            onClick={recargar}
            disabled={cargandoInicial}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <FiRefreshCw size={14} className={cargandoInicial ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Retiro #</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Fecha</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Monto</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Destino</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Motivo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cargandoInicial ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    Cargando retiros...
                  </td>
                </tr>
              ) : retiros.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No hay retiros para esta sesion.
                  </td>
                </tr>
              ) : (
                retiros.map((retiro) => (
                  <tr key={retiro.id_retiro_caja} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-900">{retiro.id_retiro_caja}</td>
                    <td className="px-4 py-2 text-gray-700">{formatFechaHora(retiro.fecha_hora)}</td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">RD$ {formatMonto(retiro.monto)}</td>
                    <td className="px-4 py-2 text-gray-700">{retiro.nombre_destino || '-'}</td>
                    <td className="px-4 py-2 text-gray-700">{retiro.motivo || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-center">
          {hasMore && (
            <button
              onClick={() => cargarRetiros({ append: true, cursor: nextCursor })}
              disabled={cargandoMas}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-color-primario text-white hover:bg-color-secundario disabled:opacity-60"
            >
              {cargandoMas ? 'Cargando...' : 'Cargar mas'}
            </button>
          )}
        </div>
      </div>
    </PlantillaModal>
  );
}
